import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { GIT_PROVIDERS } from 'consts/git';
import { useNavigateWithQueryParams } from 'hooks/useNavigateWithQueryParams';
import { useWizardAnalytics } from 'hooks/useWizardAnalytics';
import { RepositoryClient } from 'network/httpClients/RepositoryClient';
import { QUERY_KEYS } from 'network/queries/consts';
import { ROUTES } from 'routes/consts';
import { MultiStepDialog } from 'shared/MultiStepDialog';
import { spawn } from 'utils/translations';
import { getSteps } from './configuration';
import { GENERAL_TRANSLATION_KEY } from './consts';
import { GerritInstallationContext } from './gerritInstallationContext';
import { Title } from './parts/Title';

const spawnGeneral = spawn(GENERAL_TRANSLATION_KEY);

export const GerritInstallationDialog = () => {
  const [installationId, setInstallationId] = useState<string | undefined>();
  const [webhookUrl, setWebhookUrl] = useState<string | undefined>();
  const [gerritHost, setGerritHost] = useState<string | undefined>();
  const [username, setUsername] = useState<string | undefined>();
  const [apiPrefix, setApiPrefix] = useState<string | undefined>();
  const [isFinalized, setIsFinalized] = useState(false);

  const { navigateWithQueryParams } = useNavigateWithQueryParams();
  const queryClient = useQueryClient();

  const steps = getSteps({ isFinalized });

  const {
    onStepViewed,
    onStepNavigated,
    onCancel: trackClose,
    onWizardCompleted,
  } = useWizardAnalytics({
    gitProvider: GIT_PROVIDERS.GERRIT,
    wizardVersion: 'v1',
    steps,
  });

  // Tear down the abandoned (unattached) installation when the wizard unmounts
  // before repos are attached (cancel, close, or navigate-away). Refs keep the
  // unmount handler reading current values — the closure would capture stale
  // state — and the delete is idempotent, so a redundant call is harmless.
  const installationIdRef = useRef<string | undefined>(undefined);
  const isFinalizedRef = useRef(false);

  useEffect(() => {
    installationIdRef.current = installationId;
  }, [installationId]);

  useEffect(() => {
    isFinalizedRef.current = isFinalized;
  }, [isFinalized]);

  useEffect(
    () => () => {
      if (installationIdRef.current && !isFinalizedRef.current) {
        RepositoryClient.deleteGitInstallation(installationIdRef.current).catch(
          () => {},
        );
      }
    },
    [],
  );

  const close = () => {
    navigateWithQueryParams(ROUTES.INTEGRATIONS);
  };

  const onCancel = (stepIndex: number, stepId: string) => {
    trackClose(stepIndex, stepId);
    close();
  };

  const onComplete = () => {
    onWizardCompleted();
    close();
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.INSTALLATIONS_COUNTS],
    });
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.GIT_INSTALLATIONS],
    });
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.GIT_REPOSITORIES],
    });
  };

  return (
    <GerritInstallationContext.Provider
      value={{
        installationId,
        setInstallationId,
        webhookUrl,
        setWebhookUrl,
        gerritHost,
        setGerritHost,
        username,
        setUsername,
        apiPrefix,
        setApiPrefix,
        isFinalized,
        setIsFinalized,
      }}
    >
      <MultiStepDialog
        title={<Title text={spawnGeneral('title')} />}
        steps={steps}
        onCancel={onCancel}
        onComplete={onComplete}
        onStepViewed={onStepViewed}
        onStepNavigated={onStepNavigated}
        className="sm:max-w-250!"
        disableOutsideClick={true}
        disableEscapeKey={true}
      />
    </GerritInstallationContext.Provider>
  );
};
